
import React from 'react';
import { X, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { sanitizeAssetUrl, sanitizeHtmlContent, sanitizeNavigationTarget } from '../../utils/contentSecurity';

const PopupModal = ({ isOpen, onClose, content }) => {
    const navigate = useNavigate();
    const safeAction = sanitizeNavigationTarget(content?.action);
    const safeImage = sanitizeAssetUrl(content?.image);
    const safeTitle = content?.title ? sanitizeHtmlContent(content.title) : '';

    if (!isOpen || !content) return null;

    const handleAction = () => {
        if (!safeAction) return;

        if (/^https?:\/\//i.test(safeAction)) {
            window.open(safeAction, '_blank', 'noopener,noreferrer');
        } else if (/^(mailto:|tel:)/i.test(safeAction)) {
            window.location.assign(safeAction);
        } else {
            onClose(); // Close popup before navigating internally
            navigate(safeAction);
        }
    };

    return (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm" onClick={onClose}>
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-lg bg-gray-900 rounded-2xl overflow-hidden shadow-2xl border border-gray-800"
            >
                <button
                    onClick={onClose}
                    className="absolute top-3 end-3 z-20 p-2 bg-black/40 hover:bg-black/60 text-white rounded-full backdrop-blur-md transition-colors"
                >
                    <X size={20} />
                </button>

                {/* Content Container */}
                <div className="relative aspect-[4/5] sm:aspect-square w-full">
                    <div className="absolute inset-0 w-full h-full">
                        {/* Top Badge */}
                        {content.badge && (
                            <span className="absolute top-3 start-3 z-20 px-3 py-1 rounded-full bg-indigo-500/80 backdrop-blur-md text-white text-[10px] font-bold uppercase tracking-wider border border-white/20 shadow-lg">
                                {content.badge}
                            </span>
                        )}

                        {/* Background Image */}
                        {safeImage ? (
                            <img
                                src={safeImage}
                                alt={content.title || 'Popup'}
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className={`w-full h-full ${content.gradient || 'bg-gradient-to-br from-slate-700 via-gray-800 to-zinc-900'}`} />
                        )}

                        {/* Gradient Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />

                        {/* Text Content */}
                        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 flex flex-col items-center text-center">


                            {content.title && (
                                <h3 className="text-2xl md:text-3xl font-black text-white mb-2 leading-tight" dangerouslySetInnerHTML={{ __html: safeTitle }} />
                            )}

                            {content.subtitle && (
                                <p className="text-gray-300 text-sm md:text-base mb-6 max-w-xs mx-auto">
                                    {content.subtitle}
                                </p>
                            )}

                            {content.buttonText && safeAction && (
                                <button
                                    onClick={handleAction}
                                    className="w-full py-3 bg-white text-gray-900 rounded-xl font-bold text-sm hover:bg-gray-100 active:scale-95 transition-all shadow-lg flex items-center justify-center gap-2"
                                >
                                    {content.buttonText}
                                    {/^(https?:\/\/|mailto:|tel:)/i.test(safeAction) && <ExternalLink size={16} />}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default PopupModal;
